import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'src/components/common/Button/Button';
import { Dialog } from 'src/components/common/Dialog/Dialog';
import { Drawer } from 'src/components/common/Drawer/Drawer';
import { Paragraph } from 'src/components/common/Paragraph/Paragraph';
import { DrawingPannellum, PannellumCallbackArg } from 'src/components/core/DrawingPannellum/DrawingPannellum';
import { Page } from 'src/components/core/Page/Page';
import { DrawingId, drawings } from 'src/config/drawings';
import { Topic, topics } from 'src/config/topics';
import { introduction } from 'src/content/introduction';
import { filterXSS as sanitize } from 'xss';
import { marked } from 'marked';
import { sanitizeConfig } from 'src/config/sanitizer';
import { DrawingSVG } from 'src/components/core/DrawingSVG/DrawingSVG';
import { useLocalStorage, useToggle } from 'react-use';

const markdownContext = require.context('./content/markdown', true, /.md$/);
export const initialDrawingId = drawings[0].id;

export const App: React.VFC = () => {
  const [introShowing, toggleIntro] = useToggle(true);
  const [selectedDrawingId, setSelectedDrawingId] = useState<DrawingId>(initialDrawingId);
  const [selectedTopicId, setSelectedTopicId] = useState<Topic['id'] | null>(null);
  const [selectTopicMarkdown, setSelectedTopicMarkdown] = useState<string | null>(null);
  const selectedTopic = topics.find(({ id }) => id === selectedTopicId);
  const topicsToShow = topics.filter(({ parent }) => parent === selectedDrawingId);
  const [history, setHistory] = useLocalStorage<Array<DrawingId>>('history', []);

  const fetchMarkdown = useCallback(async (endpoint: string) => {
    const raw = await (await fetch(endpoint)).text();
    const md = marked(raw);
    setSelectedTopicMarkdown(md);
  }, []);

  const redirectToWebsite = () => window.open('https://www.zadkine.nl/opleidingen/startcollege', '_blank')?.focus();

  const clearSelectedTopic = () => setSelectedTopicId(null);

  const reset = () => {
    setSelectedDrawingId(drawings[0].id);
    clearSelectedTopic();
    toggleIntro();
  };

  const handleOnBackToClick = () => {
    setSelectedDrawingId(initialDrawingId);
    setSelectedTopicId(null);
  };

  const handleOnHotspotClick = useCallback((_, topic: PannellumCallbackArg) => {
    setSelectedTopicId(topic.id);
  }, []);

  const handleOnPathClick = (id: DrawingId) => setSelectedDrawingId(id);

  const navigateDrawing = useCallback((e: KeyboardEvent) => {
    const currentDrawingIndex = drawings.findIndex(({ id }) => selectedDrawingId === id);
    const prevDrawing = drawings[currentDrawingIndex - 1];
    const nextDrawing = drawings[currentDrawingIndex + 1];

    clearSelectedTopic();

    switch (e.key) {
      case 'LEFT': // IE / Edge
      case 'ArrowLeft': {
        if (prevDrawing) setSelectedDrawingId(prevDrawing.id);
        break;
      }
      case 'RIGHT': // IE / Edge
      case 'ArrowRight': {
        if (nextDrawing) setSelectedDrawingId(nextDrawing.id);
        break;
      }
      default:
        break;
    }
  }, [selectedDrawingId]);

  useEffect(() => {
    if (history?.includes(selectedDrawingId)) return;
    setHistory([...history ?? [], selectedDrawingId]);
  }, [history, selectedDrawingId, setHistory]);

  useEffect(() => {
    if (!selectedTopic) {
      setSelectedTopicMarkdown(null);
      return;
    }

    if (selectedTopic.type === 'scene') {
      setSelectedDrawingId(selectedTopic.drawingId);
      return;
    }

    const mdPath = markdownContext(selectedTopic.markdown);
    fetchMarkdown(mdPath);

  }, [fetchMarkdown, selectedTopic]);

  useEffect(() => {
    window.addEventListener('keydown', navigateDrawing);
    return () => window.removeEventListener('keydown', navigateDrawing);
  }, [navigateDrawing]);

  return (
    <Page
      header={{
        actions: [
          { label: 'Terug naar introductie', icon: 'ArrowSmLeftIcon', onClick: reset },
          { label: 'Website', onClick: redirectToWebsite },
        ],
      }}
    >
      {selectedDrawingId === 'main'
        ? <DrawingSVG onPathClick={handleOnPathClick} history={history} />
        : (
          <DrawingPannellum
            hotspots={topicsToShow}
            onBackToClick={handleOnBackToClick}
            onHotspotClick={handleOnHotspotClick}
            selectedDrawingId={selectedDrawingId}
          />
        )
      }
      {selectedTopic && selectTopicMarkdown && (
        <Drawer isOpen onClose={clearSelectedTopic} title={selectedTopic.title} position="right">
          <div
            dangerouslySetInnerHTML={{
              __html: sanitize(selectTopicMarkdown, { whiteList: sanitizeConfig, stripIgnoreTagBody: true }),
            }}
          />
        </Drawer>
      )}
      <Dialog isOpen={introShowing} onClose={toggleIntro} title="Welkom bij Zadkine Startcollege!">
        {introduction.map((x, i) => <Paragraph key={i}>{x}</Paragraph>)}
        <Button tag="button" type="button" onClick={toggleIntro} suffix="ArrowSmRightIcon">
          Ontdek het collegeplan
        </Button>
      </Dialog>
    </Page>
  );
};
